import { jwtDecode } from 'jwt-decode'
import Cookies from 'js-cookie'
import entityMapper from '../../mappers/Entity/entity.mapper'
import userMapper from '../../mappers/User/user.mapper'
import { Entity } from '../../schemas/Entity'
import { User } from '../../schemas/User'
import Utils from '../../utils'
import apiClient from '../apiClient'

type JwtDecode = {
  data: any
}

class UserService {

  /** Reads user and entity from the auth cookie when present. */
  readSessionFromCookie(): { user: User; entity: Entity } | null {
    const user = Utils.getUserByCookieAuth()
    const entity = Utils.getEntityByCookieAuth()
    if (user === undefined || entity === undefined || user.id === '') {
      return null
    }
    return { user, entity }
  }

  /** Restores session from a valid access token or refresh cookie without redirecting on failure. */
  async tryRestoreSession(): Promise<{ user: User; entity: Entity } | null> {
    if (Utils.isAccessTokenValid()) {
      const fromCookie = this.readSessionFromCookie()
      if (fromCookie !== null) {
        return fromCookie
      }
    }
    try {
      const responseApi = await apiClient.post<{ token?: string }>('/api/v2/login/refresh')
      const token = responseApi.data.token
      if (token === undefined || token === '') {
        return null
      }
      Cookies.set('auth', token)
      return this.readSessionFromCookie()
    } catch (error) {
      console.error('Session restore via refresh failed', error)
      return null
    }
  }

  /** Authenticates with email and password and stores the access token in the auth cookie. */
  async login(email: string, password: string) {
    try {
      const responseApi = await apiClient.post('/api/v2/login', { email, password })
      const response = responseApi.data
      if (!response.token) throw new Error('No recuperó token')
      const tokenData = jwtDecode<JwtDecode>(response.token)
      const userData = tokenData.data.user
      const entityData = tokenData.data.entity
      userData.token = response.token
      const user = userMapper(userData)
      const entity = entityMapper(entityData)
      Cookies.set('auth', response.token)

      return {
        user,
        entity
      }
    } catch (error) {
      throw error
    }
  }

  /** Requests a control code email for passwordless login. */
  async requestControlCode(email: string) {
    const responseApi = await apiClient.post('/api/v2/login/request-control-code', { email })
    return responseApi.data
  }

  /** Verifies the control code and stores the access token in the auth cookie. */
  async verifyControlCode(email: string, code: string) {
    const responseApi = await apiClient.post('/api/v2/login/verify-control-code', { email, code })
    const response = responseApi.data
    if (!response.token) throw new Error('No recuperó token')
    const tokenData = jwtDecode<JwtDecode>(response.token)
    const userData = tokenData.data.user
    const entityData = tokenData.data.entity
    userData.token = response.token
    const user = userMapper(userData)
    const entity = entityMapper(entityData)
    Cookies.set('auth', response.token)
    return { user, entity }
  }

  /** Lists all users for the authenticated entity. */
  async getUsers() {
    try {
      const responseApi = await apiClient.get('/api/v2/user')
      return responseApi.data
    } catch (error) {
      throw error
    }
  }

  /** Creates or updates a user for the authenticated entity. */
  async saveUser(userData: User) {
    try {
      const responseApi = await apiClient.post('/api/v2/user', userData)
      return responseApi.data
    } catch (error) {
      throw error
    }
  }

  /** Sets a password using a password_setup JWT from the public link. */
  async setPassword(token: string, password: string) {
    try {
      const responseApi = await apiClient.post('/api/v2/user/password', { token, password })
      return responseApi.data
    } catch (error) {
      throw error
    }
  }

  /** Requests a password reset email from the public login page. */
  async forgotPassword(email: string) {
    try {
      const responseApi = await apiClient.post('/api/v2/user/password/forgot', { email })
      return responseApi.data
    } catch (error) {
      throw error
    }
  }

  /** Requests a password-change email for the authenticated session user. */
  async requestPasswordChange() {
    try {
      const responseApi = await apiClient.post('/api/v2/user/password/request', {})
      return responseApi.data
    } catch (error) {
      throw error
    }
  }

  /** Revokes the server refresh session and clears the local auth cookie. */
  async logout() {
    try {
      await apiClient.post('/api/v2/login/logout')
    } catch {
      // User must be able to leave even when the network fails.
    }
    Cookies.remove('auth')
  }
}

export default UserService
